import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  GitBranch,
  Globe2,
  Info,
  Landmark,
  MoreHorizontal,
  Pencil,
  PlusCircle,
  Search,
  Trash2,
} from "lucide-react";
import dayjs from "dayjs";

import {
  PageHeader,
  AppDialog,
  FormField,
  PreviewModal,
  TextareaField,
} from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useTran } from "@/hooks";
import { usePreview } from "@/hooks/usePreview";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";

import {
  ReportPreviewProvider,
  useReportPreviewContext,
} from "@/features/esg/hooks/reportPreview.context";
import {
  exportPreviewReportToExcel,
  exportPreviewReportToPdf,
  exportPreviewReportToWord,
} from "@/features/esg/services/report-preview-word-export.service";
import {
  buildMockPreviewContent,
  buildOrgNodeMaps,
  buildSelectedRelationshipTree,
  loadMockPreviewData,
} from "@/features/esg/services/loadPreviewData";
import { Can } from "@/hooks/useCan/Can";
import { reportPreviewService } from "@/features/esg/services";
import { useAuthStore } from "@/features/auth";
import { useOrganizationStore } from "@/features/organization";
import {
  isSuperAdminRole,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";

const ReportListPageContent = () => {
  const { t } = useTran();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const isAdmin = isSuperAdminRole(user?.role);
  const myOrgIds = useMemo(() => {
    const raw = user?.organizationUnitId;
    return (Array.isArray(raw) ? raw : raw ? [raw] : [])
      .map(normalizeOrgId)
      .filter(Boolean);
  }, [user?.organizationUnitId]);
  const canEditDeleteReport = useCallback(
    (report) => {
      if (isAdmin) return true;
      const userId = String(user?.id || user?._id || "");
      if (userId && String(report?.createdBy || "") === userId) return true;
      const creatorOrgIds = Array.isArray(report?.createdByOrgIds)
        ? report.createdByOrgIds.map(String)
        : [];
      if (myOrgIds.length !== creatorOrgIds.length) return false;
      const creatorSet = new Set(creatorOrgIds);
      return myOrgIds.every((id) => creatorSet.has(id));
    },
    [isAdmin, myOrgIds, user],
  );
  const {
    tree: orgTree,
    loading: orgLoading,
    fetchHierarchy,
  } = useOrganizationStore();

  // The full org hierarchy is only needed to resolve names inside the
  // "View organizations" popover — fetch it lazily on first open instead of
  // unconditionally on page mount, so the list itself loads faster.
  const handleViewOrganizationsClick = useCallback(() => {
    if (!orgTree?.length && !orgLoading) {
      fetchHierarchy?.().catch(() => {});
    }
  }, [fetchHierarchy, orgLoading, orgTree?.length]);

  const orgMaps = useMemo(() => buildOrgNodeMaps(orgTree || []), [orgTree]);

  const ORG_LEVEL_ICON = {
    parent_company: { Icon: Building2, color: "text-indigo-600" },
    level_1: { Icon: Building2, color: "text-sky-600" },
    level_2: { Icon: Landmark, color: "text-emerald-600" },
    level_3: { Icon: GitBranch, color: "text-amber-600" },
    country: { Icon: Globe2, color: "text-emerald-600" },
  };

  const renderOrgTree = useCallback(
    (nodes = [], depth = 0) =>
      nodes.map((node) => {
        const { Icon, color } =
          ORG_LEVEL_ICON[node.level] || ORG_LEVEL_ICON.level_1;
        return (
          <div key={node.id || node.title}>
            <div
              className="flex items-center gap-2 rounded-md py-1 text-xs text-foreground hover:bg-muted/60"
              style={{ paddingLeft: `${8 + depth * 16}px` }}
            >
              <span
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-background shadow-sm ${color}`}
              >
                <Icon className="h-3 w-3" />
              </span>
              <span>{node.title}</span>
            </div>
            {node.children?.length
              ? renderOrgTree(node.children, depth + 1)
              : null}
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const {
    reports,
    reportsLoading,
    reportsError,
    completeReport,
    updateReport,
    deleteReport,
    fetchReports,
  } = useReportPreviewContext();
  const [editReport, setEditReport] = useState(null);
  const [isSavingEditReport, setIsSavingEditReport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("completed");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [frameworkNameByCode, setFrameworkNameByCode] = useState(
    () => new Map(),
  );

  useEffect(() => {
    let isMounted = true;

    reportPreviewService
      .getFilterOptions({}, { throwOnError: false })
      .then((data) => {
        if (!isMounted) return;
        const frameworks = Array.isArray(data?.assignedFrameworks)
          ? data.assignedFrameworks
          : [];
        setFrameworkNameByCode(
          new Map(frameworks.map((fw) => [fw.code, fw.name])),
        );
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  useEffect(() => {
    if (location.state?.reportStatus === "completed") {
      setActiveTab("completed");
    }
  }, [location.state]);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    presentedTo: "",
    includeCoverPage: true,
    includeContentPage: true,
    isShared: false,
  });

  useEffect(() => {
    if (!editReport) return;
    setEditForm({
      name: editReport.name || "",
      description: editReport.description || "",
      presentedTo: editReport.presentedTo || "",
      includeCoverPage: Boolean(editReport.includeCoverPage),
      includeContentPage: Boolean(editReport.includeContentPage),
      isShared:
        editReport.status === "completed"
          ? Boolean(editReport.isShared)
          : false,
    });
  }, [editReport]);

  useEffect(() => {
    fetchReports?.().catch(() => {});
  }, [fetchReports]);

  const toggleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
      setCurrentPage(1);
    },
    [sortKey],
  );

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => {
      let aVal, bVal;
      if (sortKey === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else if (sortKey === "presentedTo") {
        aVal = (a.presentedTo || "").toLowerCase();
        bVal = (b.presentedTo || "").toLowerCase();
      } else if (sortKey === "startDate") {
        aVal = a.startDate ? new Date(a.startDate).getTime() : 0;
        bVal = b.startDate ? new Date(b.startDate).getTime() : 0;
      } else {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [reports, sortKey, sortDir]);

  const draftReports = useMemo(
    () =>
      sortedReports.filter(
        (report) =>
          String(report.status || "draft").toLowerCase() !== "completed",
      ),
    [sortedReports],
  );

  const completedReports = useMemo(
    () =>
      sortedReports.filter(
        (report) => String(report.status || "").toLowerCase() === "completed",
      ),
    [sortedReports],
  );

  const visibleReports =
    activeTab === "completed" ? completedReports : draftReports;

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const searchedReports = useMemo(() => {
    if (!normalizedSearchTerm) return visibleReports;
    return visibleReports.filter((report) => {
      const frameworkNames = Array.isArray(report.frameworks)
        ? report.frameworks.map((code) => frameworkNameByCode.get(code) || code)
        : [];
      return [
        report.name,
        report.presentedTo,
        report.createdByName,
        report.updatedByName,
        ...(report.entityLabels || []),
        ...frameworkNames,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearchTerm);
    });
  }, [visibleReports, normalizedSearchTerm, frameworkNameByCode]);

  const totalPages = Math.max(1, Math.ceil(searchedReports.length / pageSize));

  const paginatedVisibleReports = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return searchedReports.slice(start, start + pageSize);
  }, [searchedReports, currentPage, pageSize]);

  const formatDateRange = (report) => {
    if (!report?.startDate || !report?.endDate) return "-";
    const startLabel = dayjs(report.startDate).isValid()
      ? dayjs(report.startDate).format("MMM YYYY")
      : report.startDate;
    const endLabel = dayjs(report.endDate).isValid()
      ? dayjs(report.endDate).format("MMM YYYY")
      : report.endDate;
    return `${startLabel} – ${endLabel}`;
  };

  const {
    previewOpen,
    previewLoading,
    previewError,
    previewSource,
    previewContent,
    openPreview,
    closePreview,
    onPreviewOpenChange,
  } = usePreview({
    loadPreviewData: loadMockPreviewData,
    // Mock preview content is used temporarily for modal design iteration.
    buildPreviewContent: ({ source, data }) =>
      buildMockPreviewContent({
        report: source,
        data,
        t,
        frameworkNameByCode,
      }),
    getTitle: (report) => report?.name || t("Preview"),
    getDescription: () =>
      t("Review the current report content before exporting."),
  });

  const handleExport = async (report, type) => {
    const format = String(type || "").toLowerCase();
    if (format === "excel") {
      showToast.promise(
        exportPreviewReportToExcel({ report, t, frameworkNameByCode }),
        {
          loading: t("Preparing Excel document..."),
          success: (fileName) => t('Exported "{{fileName}}"', { fileName }),
          error: (error) =>
            error?.message || t("Unable to export report as Excel."),
        },
      );
      return;
    }

    if (format === "pdf") {
      showToast.promise(
        exportPreviewReportToPdf({ report, t, frameworkNameByCode }),
        {
          loading: t("Preparing PDF document..."),
          success: (fileName) => t('Exported "{{fileName}}"', { fileName }),
          error: (error) =>
            error?.message || t("Unable to export report as PDF."),
        },
      );
      return;
    }

    if (format === "word") {
      showToast.promise(
        exportPreviewReportToWord({ report, t, frameworkNameByCode }),
        {
          loading: t("Exporting Word document..."),
          success: (fileName) => t('Exported "{{fileName}}"', { fileName }),
          error: (error) =>
            error?.message || t("Unable to export report as Word."),
        },
      );
      return;
    }
  };

  const handleEditSave = async () => {
    if (isSavingEditReport) return;
    if (!editReport) return;
    if (!editForm.name.trim()) {
      showToast.error(t("Report name is required"));
      return;
    }
    if (!editForm.presentedTo?.trim()) {
      showToast.error(t("Presented to is required"));
      return;
    }
    setIsSavingEditReport(true);
    try {
      const updated = await updateReport(editReport.id, {
        name: editForm.name.trim(),
        description: editForm.description?.trim?.() || "",
        presentedTo: editForm.presentedTo?.trim?.() || "",
        includeCoverPage: editForm.includeCoverPage,
        includeContentPage: editForm.includeContentPage,
        ...(editReport.status === "completed" && {
          isShared: editForm.isShared,
        }),
      });
      if (!updated) {
        showToast.error(t("Unable to update report"));
        return;
      }
      showToast.success(t("Report updated"));
      setEditReport(null);
    } finally {
      setIsSavingEditReport(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("ESG Report Lists")}
        icon={FileText}
        description={t(
          "A list of ESG reports generated based on selected preview filters, enabling streamlined review, management, and export of reporting data.",
        )}
        actions={
          <Can
            resource="esg_reporting"
            action="create"
            userPermissions={permissions}
          >
            <Button onClick={() => navigate(PATHS.ESG_REPORT_PREVIEW)}>
              <PlusCircle />
              {t("Preview Data To Create Report")}
            </Button>
          </Can>
        }
      />

      <Can
        resource="esg_reporting"
        action="view"
        userPermissions={permissions}
        showMessage={true}
      >
        {reportsError ? (
          <div className="flex flex-col items-center gap-3 rounded-radius border border-dashed border-border bg-card/40 p-10 text-center">
            <span className="text-sm text-destructive">{reportsError}</span>
          </div>
        ) : !reportsLoading && sortedReports.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-radius border border-dashed border-border bg-card/40 p-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-radius bg-primary/10 text-primary">
              <FileText className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-semibold text-foreground">
                {t("No reports yet")}
              </p>
              <Can
                resource="esg_reporting"
                action="create"
                userPermissions={permissions}
              >
                <p className="text-sm text-muted-foreground">
                  {t("Create a preview data to see it appear here.")}
                </p>
              </Can>
            </div>
            <Can
              resource="esg_reporting"
              action="create"
              userPermissions={permissions}
            >
              <Button onClick={() => navigate(PATHS.ESG_REPORT_PREVIEW)}>
                <PlusCircle />
                {t("Preview Data To Create Report")}
              </Button>
            </Can>
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-3"
          >
            <div className="flex items-center justify-between gap-3">
              <TabsList>
                <TabsTrigger value="completed">{t("Completed")}</TabsTrigger>
                <TabsTrigger value="draft">{t("Draft")}</TabsTrigger>
              </TabsList>

              <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("Search by name")}
                  className="pl-9"
                />
              </div>
            </div>

            <TabsContent value="draft" className="mt-0">
              {!reportsLoading && visibleReports.length === 0 ? (
                <div className="rounded-radius border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
                  {t("No draft reports yet.")}
                </div>
              ) : !reportsLoading && searchedReports.length === 0 ? (
                <div className="rounded-radius border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
                  {t("No reports match your search.")}
                </div>
              ) : (
                <div className="rounded-radius border border-border bg-card shadow-sm">
                  <Table className="[&_td]:whitespace-normal [&_th]:whitespace-normal">
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("name")}
                          >
                            {t("Report Name")}
                            {sortKey === "name" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("presentedTo")}
                          >
                            {t("Presented To")}
                            {sortKey === "presentedTo" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Frameworks")}</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("startDate")}
                          >
                            {t("Date range")}
                            {sortKey === "startDate" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Consolidation")}</TableHead>
                        <TableHead>{t("Include all entities")}</TableHead>
                        <TableHead>{t("Cover page")}</TableHead>
                        <TableHead>{t("Content page")}</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("createdAt")}
                          >
                            {t("Created")}
                            {sortKey === "createdAt" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Created By")}</TableHead>
                        <TableHead>{t("Updated By")}</TableHead>
                        <TableHead className="text-right">
                          {t("Actions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportsLoading
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Skeleton className="h-4 w-32" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-28" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="ml-auto h-4 w-8" />
                              </TableCell>
                            </TableRow>
                          ))
                        : paginatedVisibleReports.map((report) => (
                            <TableRow key={report.id}>
                              <TableCell>
                                {(() => {
                                  const labels = report.entityLabels?.length
                                    ? report.entityLabels
                                    : [];
                                  return (
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-foreground">
                                        {report.name}
                                      </span>
                                      <Badge variant="outline">
                                        {t("Draft")}
                                      </Badge>
                                      {labels.length ? (
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              onClick={
                                                handleViewOrganizationsClick
                                              }
                                              className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                              aria-label={t(
                                                "View organizations",
                                              )}
                                            >
                                              <Info className="h-3.5 w-3.5" />
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            className="w-80 p-3"
                                            align="start"
                                          >
                                            <div className="text-xs font-semibold text-foreground">
                                              {t("Organization")}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                              {report.includeAllEntities
                                                ? t(
                                                    "Includes all child entities.",
                                                  )
                                                : t(
                                                    "Selected organization only.",
                                                  )}
                                            </p>
                                            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
                                              {(() => {
                                                // Org names only resolve once the (lazily-fetched)
                                                // hierarchy has loaded — before that, orgMaps is
                                                // empty and would render raw org IDs instead of
                                                // names, so fall back to the labels below until
                                                // it's ready.
                                                const tree =
                                                  orgTree?.length && !orgLoading
                                                    ? buildSelectedRelationshipTree(
                                                        {
                                                          selected:
                                                            report.companies ||
                                                            [],
                                                          buSide: report.buSide,
                                                          includeAllEntities:
                                                            Boolean(
                                                              report.includeAllEntities,
                                                            ),
                                                          maps: orgMaps,
                                                          t,
                                                        },
                                                      )
                                                    : [];
                                                return tree.length
                                                  ? renderOrgTree(tree)
                                                  : labels.map((label) => (
                                                      <span
                                                        key={label}
                                                        className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-foreground hover:bg-muted/60"
                                                      >
                                                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-background text-sky-600 shadow-sm">
                                                          <Building2 className="h-3 w-3" />
                                                        </span>
                                                        {label}
                                                      </span>
                                                    ));
                                              })()}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {report.presentedTo || (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {Array.isArray(report.frameworks) &&
                                report.frameworks.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {report.frameworks.map((code) => (
                                      <Badge
                                        key={code}
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {frameworkNameByCode.get(code) || code}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>{formatDateRange(report)}</TableCell>
                              <TableCell>
                                {report.consolidation ? t("Yes") : t("No")}
                              </TableCell>
                              <TableCell>
                                {report.includeAllEntities == null
                                  ? "-"
                                  : report.includeAllEntities
                                    ? t("Yes")
                                    : t("No")}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {report.includeCoverPage ? t("Yes") : t("No")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {report.includeContentPage
                                    ? t("Yes")
                                    : t("No")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {report.createdAt
                                  ? dayjs(report.createdAt).format(
                                      "MMM D, YYYY",
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {report.createdBy ===
                                (user?.id || user?._id?.toString?.())
                                  ? t("You")
                                  : report.createdByName || (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    )}
                              </TableCell>
                              <TableCell>
                                {!report.updatedBy ? (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                ) : report.updatedBy ===
                                  (user?.id || user?._id?.toString?.()) ? (
                                  t("You")
                                ) : (
                                  report.updatedByName || (
                                    <span className="text-muted-foreground">
                                      -
                                    </span>
                                  )
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8"
                                        aria-label={t("Actions")}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-52"
                                    >
                                      <DropdownMenuLabel>
                                        {t("Actions")}
                                      </DropdownMenuLabel>
                                      <DropdownMenuItem
                                        onClick={() => openPreview(report)}
                                      >
                                        <Eye className="h-4 w-4" />
                                        <span>{t("Preview")}</span>
                                      </DropdownMenuItem>
                                      <Can
                                        resource="esg_reporting"
                                        action="update"
                                        userPermissions={permissions}
                                      >
                                        <DropdownMenuItem
                                          onClick={() =>
                                            setCompleteTarget(report)
                                          }
                                        >
                                          <CheckCircle2 className="h-4 w-4" />
                                          <span>{t("Mark as Completed")}</span>
                                        </DropdownMenuItem>
                                      </Can>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() =>
                                          handleExport(report, "Excel")
                                        }
                                      >
                                        <FileSpreadsheet className="h-4 w-4" />
                                        <div>
                                          <p className="text-sm">
                                            {t("Export to Excel")}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {t("Spreadsheet format (.xlsx)")}
                                          </p>
                                        </div>
                                      </DropdownMenuItem>
                                      {/* Word export is temporarily disabled for ESG report preview.
                                <DropdownMenuItem
                                  onClick={() => handleExport(report, "Word")}
                                >
                                  <FileText className="h-4 w-4" />
                                  <div>
                                    <p className="text-sm">
                                      {t("Export to Word")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {t("Document format (.docx)")}
                                    </p>
                                  </div>
                                </DropdownMenuItem>
                                */}
                                      {/* PDF export is temporarily disabled for ESG report preview.
                                <DropdownMenuItem
                                  onClick={() => handleExport(report, "PDF")}
                                >
                                  <FileText className="h-4 w-4" />
                                  <div>
                                    <p className="text-sm">
                                      {t("Export to PDF")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {t("Print-ready format (.pdf)")}
                                    </p>
                                  </div>
                                </DropdownMenuItem>
                                */}
                                      {canEditDeleteReport(report) && (
                                        <Can
                                          resource="esg_reporting"
                                          action="update"
                                          userPermissions={permissions}
                                        >
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() =>
                                              navigate(
                                                PATHS.ESG_REPORT_PREVIEW,
                                                {
                                                  state: {
                                                    previewReport: report,
                                                    editMode: "draft",
                                                  },
                                                },
                                              )
                                            }
                                          >
                                            <Pencil className="h-4 w-4" />
                                            <span>{t("Edit")}</span>
                                          </DropdownMenuItem>
                                        </Can>
                                      )}
                                      {canEditDeleteReport(report) && (
                                        <Can
                                          resource="esg_reporting"
                                          action="delete"
                                          userPermissions={permissions}
                                        >
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            onClick={() =>
                                              setDeleteTarget(report)
                                            }
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            <span>{t("Delete")}</span>
                                          </DropdownMenuItem>
                                        </Can>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between border-t border-border px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{t("Rows per page")}</span>
                      <Select
                        value={String(pageSize)}
                        onValueChange={(v) => {
                          setPageSize(Number(v));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger size="sm" className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 20, 50].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span>
                        {searchedReports.length === 0
                          ? t("0 results")
                          : t("{{from}}–{{to}} of {{total}}", {
                              from: (currentPage - 1) * pageSize + 1,
                              to: Math.min(
                                currentPage * pageSize,
                                searchedReports.length,
                              ),
                              total: searchedReports.length,
                            })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        aria-label={t("First page")}
                      >
                        «
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        aria-label={t("Previous page")}
                      >
                        ‹
                      </Button>
                      <span className="min-w-[90px] text-center text-sm text-muted-foreground">
                        {t("Page {{page}} of {{total}}", {
                          page: currentPage,
                          total: totalPages,
                        })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        aria-label={t("Next page")}
                      >
                        ›
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        aria-label={t("Last page")}
                      >
                        »
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-0">
              {!reportsLoading && visibleReports.length === 0 ? (
                <div className="rounded-radius border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
                  {t("No completed reports yet.")}
                </div>
              ) : !reportsLoading && searchedReports.length === 0 ? (
                <div className="rounded-radius border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
                  {t("No reports match your search.")}
                </div>
              ) : (
                <div className="rounded-radius border border-border bg-card shadow-sm">
                  <Table className="[&_td]:whitespace-normal [&_th]:whitespace-normal">
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("name")}
                          >
                            {t("Report Name")}
                            {sortKey === "name" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("presentedTo")}
                          >
                            {t("Presented To")}
                            {sortKey === "presentedTo" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Frameworks")}</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("startDate")}
                          >
                            {t("Date range")}
                            {sortKey === "startDate" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Consolidation")}</TableHead>
                        <TableHead>{t("Include all entities")}</TableHead>
                        <TableHead>{t("Cover page")}</TableHead>
                        <TableHead>{t("Content page")}</TableHead>
                        <TableHead>{t("Shared")}</TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("createdAt")}
                          >
                            {t("Created")}
                            {sortKey === "createdAt" ? (
                              sortDir === "asc" ? (
                                <ChevronUp className="h-3 w-3" />
                              ) : (
                                <ChevronDown className="h-3 w-3" />
                              )
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 opacity-40" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead>{t("Created By")}</TableHead>
                        <TableHead>{t("Updated By")}</TableHead>
                        <TableHead className="text-right">
                          {t("Actions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportsLoading
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Skeleton className="h-4 w-32" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-28" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-16" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-20" />
                              </TableCell>
                              <TableCell className="text-right">
                                <Skeleton className="ml-auto h-4 w-8" />
                              </TableCell>
                            </TableRow>
                          ))
                        : paginatedVisibleReports.map((report) => (
                            <TableRow key={report.id}>
                              <TableCell>
                                {(() => {
                                  const labels = report.entityLabels?.length
                                    ? report.entityLabels
                                    : [];
                                  return (
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-foreground">
                                        {report.name}
                                      </span>
                                      {labels.length ? (
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              onClick={
                                                handleViewOrganizationsClick
                                              }
                                              className="flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                              aria-label={t(
                                                "View organizations",
                                              )}
                                            >
                                              <Info className="h-3.5 w-3.5" />
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            className="w-80 p-3"
                                            align="start"
                                          >
                                            <div className="text-xs font-semibold text-foreground">
                                              {t("Organization")}
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                              {report.includeAllEntities
                                                ? t(
                                                    "Includes all child entities.",
                                                  )
                                                : t(
                                                    "Selected organization only.",
                                                  )}
                                            </p>
                                            <div className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
                                              {(() => {
                                                // Org names only resolve once the (lazily-fetched)
                                                // hierarchy has loaded — before that, orgMaps is
                                                // empty and would render raw org IDs instead of
                                                // names, so fall back to the labels below until
                                                // it's ready.
                                                const tree =
                                                  orgTree?.length && !orgLoading
                                                    ? buildSelectedRelationshipTree(
                                                        {
                                                          selected:
                                                            report.companies ||
                                                            [],
                                                          buSide: report.buSide,
                                                          includeAllEntities:
                                                            Boolean(
                                                              report.includeAllEntities,
                                                            ),
                                                          maps: orgMaps,
                                                          t,
                                                        },
                                                      )
                                                    : [];
                                                return tree.length
                                                  ? renderOrgTree(tree)
                                                  : labels.map((label) => (
                                                      <span
                                                        key={label}
                                                        className="flex items-center gap-2 rounded-md px-2 py-1 text-xs text-foreground hover:bg-muted/60"
                                                      >
                                                        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-background text-sky-600 shadow-sm">
                                                          <Building2 className="h-3 w-3" />
                                                        </span>
                                                        {label}
                                                      </span>
                                                    ));
                                              })()}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      ) : null}
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                {report.presentedTo || (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {Array.isArray(report.frameworks) &&
                                report.frameworks.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {report.frameworks.map((code) => (
                                      <Badge
                                        key={code}
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {frameworkNameByCode.get(code) || code}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>{formatDateRange(report)}</TableCell>
                              <TableCell>
                                {report.consolidation ? t("Yes") : t("No")}
                              </TableCell>
                              <TableCell>
                                {report.includeAllEntities == null
                                  ? "-"
                                  : report.includeAllEntities
                                    ? t("Yes")
                                    : t("No")}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {report.includeCoverPage ? t("Yes") : t("No")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {report.includeContentPage
                                    ? t("Yes")
                                    : t("No")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {report.isShared ? t("Yes") : t("No")}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {report.createdAt
                                  ? dayjs(report.createdAt).format(
                                      "MMM D, YYYY",
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                {report.createdBy ===
                                (user?.id || user?._id?.toString?.())
                                  ? t("You")
                                  : report.createdByName || (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    )}
                              </TableCell>
                              <TableCell>
                                {!report.updatedBy ? (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                ) : report.updatedBy ===
                                  (user?.id || user?._id?.toString?.()) ? (
                                  t("You")
                                ) : (
                                  report.updatedByName || (
                                    <span className="text-muted-foreground">
                                      -
                                    </span>
                                  )
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8"
                                        aria-label={t("Actions")}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="w-52"
                                    >
                                      <DropdownMenuLabel>
                                        {t("Actions")}
                                      </DropdownMenuLabel>
                                      <DropdownMenuItem
                                        onClick={() => openPreview(report)}
                                      >
                                        <Eye className="h-4 w-4" />
                                        <span>{t("Preview")}</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() =>
                                          handleExport(report, "Excel")
                                        }
                                      >
                                        <FileSpreadsheet className="h-4 w-4" />
                                        <div>
                                          <p className="text-sm">
                                            {t("Export to Excel")}
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {t("Spreadsheet format (.xlsx)")}
                                          </p>
                                        </div>
                                      </DropdownMenuItem>
                                      {/* Word export is temporarily disabled for ESG report preview.
                                <DropdownMenuItem
                                  onClick={() => handleExport(report, "Word")}
                                >
                                  <FileText className="h-4 w-4" />
                                  <div>
                                    <p className="text-sm">
                                      {t("Export to Word")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {t("Document format (.docx)")}
                                    </p>
                                  </div>
                                </DropdownMenuItem>
                                */}
                                      {/* PDF export is temporarily disabled for ESG report preview.
                                <DropdownMenuItem
                                  onClick={() => handleExport(report, "PDF")}
                                >
                                  <FileText className="h-4 w-4" />
                                  <div>
                                    <p className="text-sm">
                                      {t("Export to PDF")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {t("Print-ready format (.pdf)")}
                                    </p>
                                  </div>
                                </DropdownMenuItem>
                                */}
                                      {canEditDeleteReport(report) && (
                                        <Can
                                          resource="esg_reporting"
                                          action="update"
                                          userPermissions={permissions}
                                        >
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() =>
                                              setEditReport(report)
                                            }
                                          >
                                            <Pencil className="h-4 w-4" />
                                            <span>{t("Edit")}</span>
                                          </DropdownMenuItem>
                                        </Can>
                                      )}
                                      {canEditDeleteReport(report) && (
                                        <Can
                                          resource="esg_reporting"
                                          action="delete"
                                          userPermissions={permissions}
                                        >
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            onClick={() =>
                                              setDeleteTarget(report)
                                            }
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            <span>{t("Delete")}</span>
                                          </DropdownMenuItem>
                                        </Can>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between border-t border-border px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{t("Rows per page")}</span>
                      <Select
                        value={String(pageSize)}
                        onValueChange={(v) => {
                          setPageSize(Number(v));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger size="sm" className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 20, 50].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span>
                        {searchedReports.length === 0
                          ? t("0 results")
                          : t("{{from}}–{{to}} of {{total}}", {
                              from: (currentPage - 1) * pageSize + 1,
                              to: Math.min(
                                currentPage * pageSize,
                                searchedReports.length,
                              ),
                              total: searchedReports.length,
                            })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        aria-label={t("First page")}
                      >
                        «
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        aria-label={t("Previous page")}
                      >
                        ‹
                      </Button>
                      <span className="min-w-[90px] text-center text-sm text-muted-foreground">
                        {t("Page {{page}} of {{total}}", {
                          page: currentPage,
                          total: totalPages,
                        })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        aria-label={t("Next page")}
                      >
                        ›
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        aria-label={t("Last page")}
                      >
                        »
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </Can>

      <PreviewModal
        open={previewOpen}
        onOpenChange={onPreviewOpenChange}
        title={previewContent?.title || t("Preview")}
        description={
          previewContent?.description ||
          t("Review the current report content before exporting.")
        }
        loading={previewLoading}
        error={previewError}
        content={previewContent}
        emptyMessage={t("No preview data available.")}
        footer={
          <>
            <Button variant="outline" onClick={closePreview}>
              {t("Close")}
            </Button>
            <Can
              resource="esg_reporting"
              action="view"
              userPermissions={permissions}
            >
              <Button
                variant="outline"
                disabled={!previewSource}
                onClick={() => handleExport(previewSource, "Excel")}
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t("Export to Excel")}
              </Button>
            </Can>
            {/* Word export is temporarily disabled for ESG report preview.
            <Can
              resource="esg_reporting"
              action="view"
              userPermissions={permissions}
            >
              <Button
                variant="outline"
                disabled={!previewSource}
                onClick={() => handleExport(previewSource, "Word")}
              >
                <FileText className="h-4 w-4" />
                {t("Export to Word")}
              </Button>
            </Can>
            */}
            {/* PDF export is temporarily disabled for ESG report preview.
            <Can
              resource="esg_reporting"
              action="view"
              userPermissions={permissions}
            >
              <Button
                disabled={!previewSource}
                onClick={() => handleExport(previewSource, "PDF")}
              >
                <Download className="h-4 w-4" />
                {t("Export to PDF")}
              </Button>
            </Can>
            */}
          </>
        }
      />

      <AppDialog
        open={Boolean(editReport)}
        onOpenChange={(open) => !open && setEditReport(null)}
        title={t("Edit Report")}
        description={t("Update the report details below.")}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditReport(null)}>
              {t("Cancel")}
            </Button>
            <Button onClick={handleEditSave} disabled={isSavingEditReport}>
              {isSavingEditReport ? t("Saving...") : t("Save")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField
            label={t("Report Name")}
            value={editForm.name}
            required
            onChange={(event) =>
              setEditForm((prev) => ({ ...prev, name: event.target.value }))
            }
          />
          <TextareaField
            label={t("Description")}
            value={editForm.description}
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            placeholder={t("Add a desciption for this report preview.")}
            rows={3}
            maxLength={240}
            showCount
          />
          <FormField
            label={t("Presented To")}
            value={editForm.presentedTo}
            required
            onChange={(event) =>
              setEditForm((prev) => ({
                ...prev,
                presentedTo: event.target.value,
              }))
            }
            placeholder={t("Organization or recipient name")}
          />
          {editReport?.status === "completed" && (
            <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50/40 px-3 py-2.5 dark:border-amber-700 dark:bg-amber-950/20">
              <Checkbox
                id="isShared-edit"
                checked={editForm.isShared}
                onCheckedChange={(checked) =>
                  setEditForm((prev) => ({
                    ...prev,
                    isShared: Boolean(checked),
                  }))
                }
                className="mt-0.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              />
              <div className="space-y-0.5">
                <label
                  htmlFor="isShared-edit"
                  className="text-sm font-medium text-amber-800 dark:text-amber-300 cursor-pointer"
                >
                  {t("Share this report")}
                </label>
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t(
                    "When enabled, this report will be visible to other users in your organization.",
                  )}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 rounded-radius border border-border bg-muted/30 px-3 py-2">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                {t("Include cover page")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("Adds a title page to the exported preview.")}
              </p>
            </div>
            <Switch
              checked={editForm.includeCoverPage}
              onCheckedChange={(checked) =>
                setEditForm((prev) => ({
                  ...prev,
                  includeCoverPage: Boolean(checked),
                }))
              }
            />
          </div>
          <div className="flex items-center justify-between gap-4 rounded-radius border border-border bg-muted/30 px-3 py-2">
            <div className="space-y-1">
              <Label className="text-sm font-medium">
                {t("Include content page")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("Adds a contents overview page to the preview output.")}
              </p>
            </div>
            <Switch
              checked={editForm.includeContentPage}
              onCheckedChange={(checked) =>
                setEditForm((prev) => ({
                  ...prev,
                  includeContentPage: Boolean(checked),
                }))
              }
            />
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={Boolean(completeTarget)}
        onOpenChange={(open) => !open && setCompleteTarget(null)}
        title={t("Mark as Completed")}
        description={t(
          "Completed reports are stored as fixed snapshots and are no longer editable.",
        )}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCompleteTarget(null)}>
              {t("Cancel")}
            </Button>
            <Can
              resource="esg_reporting"
              action="update"
              userPermissions={permissions}
            >
              <Button
                onClick={async () => {
                  const completed = await completeReport(completeTarget?.id);
                  if (completed) {
                    showToast.success(t("Report marked as completed"));
                    setCompleteTarget(null);
                    setActiveTab("completed");
                  } else {
                    showToast.error(t("Unable to mark report as completed"));
                  }
                }}
              >
                {t("Mark as Completed")}
              </Button>
            </Can>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t(
            'Mark "{{name}}" as completed? The exported data will use a fixed snapshot.',
            {
              name: completeTarget?.name || "",
            },
          )}
        </p>
      </AppDialog>

      <AppDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("Delete Preview Report")}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={isDeletingReport}
              onClick={async () => {
                if (!deleteTarget?.id || isDeletingReport) return;
                setIsDeletingReport(true);
                try {
                  const success = await deleteReport(deleteTarget.id);
                  if (success) {
                    showToast.success(t("Report deleted"));
                    setDeleteTarget(null);
                  } else {
                    showToast.error(t("Unable to delete report"));
                  }
                } finally {
                  setIsDeletingReport(false);
                }
              }}
            >
              {isDeletingReport ? t("Deleting...") : t("Delete")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t(
            `Are you sure you want to delete "{{name}}"? This action cannot be undone.`,
            { name: deleteTarget?.name },
          )}
        </p>
      </AppDialog>
    </div>
  );
};

const ReportListPage = () => (
  <ReportPreviewProvider>
    <ReportListPageContent />
  </ReportPreviewProvider>
);

export default ReportListPage;
